
import type { PropType } from 'vue';

import type { Cropper, ImageSize } from 'vue-advanced-cropper';

import type { ErrorsMap } from '@/types';

import {
  defineComponent,
  ref,
  computed,
} from 'vue';

import PrimeVueDialog from 'primevue/dialog';

import { useToast } from 'primevue/usetoast';

import { Cropper as CropperComponent } from 'vue-advanced-cropper';

import 'vue-advanced-cropper/dist/style.css';

import Pica from 'pica';

export default defineComponent({
  components: {
    PrimeVueDialog,
    Cropper: CropperComponent,
  },
  props: {
    modelValue: {
      type: String,
    },
    imageUrl: {
      type: String,
    },
    name: {
      type: String,
    },
    errorsMap: {
      type: Object as PropType<ErrorsMap>,
      default: () => ({}),
    },
    label: {
      type: String,
    },
    previewMaxWidth: {
      type: Number,
    },
    stencilProps: {
      type: Object as PropType<Record<string, unknown>>,
    },
    stencilSize: {
      type: Object as PropType<{ width: number; height: number }>,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const dialog = ref<boolean>(false);

    const toast = useToast();

    const cropper = ref<typeof Cropper|null>(null);

    const imageBase64 = ref<ArrayBuffer|string|null>(null);

    const imageBase64Cropped = ref<string|null>(null);

    const onFileChange = (event: Event): void => {
      const { files } = event.target as HTMLInputElement;

      if (!files || !files.length) {
        return;
      }

      const file = files[0];

      const reader = new FileReader();

      reader.onloadend = (): void => {
        imageBase64.value = reader.result;
      };

      reader.readAsDataURL(file);
    };

    const onCropperChange = (attributes: { canvas: HTMLCanvasElement }): void => {
      imageBase64Cropped.value = attributes.canvas.toDataURL();
    };

    const onCropperReady = (): void => {
      if (!props.stencilSize) {
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { image }: { image: ImageSize } = cropper.value as any;

      if (image.width < props.stencilSize.width || image.height < props.stencilSize.height) {
        imageBase64.value = null;

        toast.add({
          severity: 'error',
          summary: 'Image is too small',
          detail: 'The selected image is smaller than the required size',
          life: 10000,
        });
      }
    };

    const onZoom = (factor: number): void => {
      if (!cropper.value) return;

      cropper.value.zoom(factor);
    };

    const save = async (): Promise<void> => {
      let dataUrl = null;

      if (props.stencilSize) {
        const pica = new Pica();

        const { canvas } = cropper.value?.getResult();

        const picaCanvas = document.createElement('canvas');

        picaCanvas.width = props.stencilSize.width;

        picaCanvas.height = props.stencilSize.height;

        await pica.resize(canvas, picaCanvas);

        dataUrl = picaCanvas.toDataURL();
      } else {
        dataUrl = imageBase64Cropped.value;
      }

      emit('update:modelValue', dataUrl);

      imageBase64.value = null;

      imageBase64Cropped.value = null;

      dialog.value = false;
    };

    const defaultSize = computed(() => (props.stencilSize ? { width: props.stencilSize.width, height: props.stencilSize.height } : undefined));

    return {
      cropper,
      dialog,
      imageBase64,
      onFileChange,
      onCropperChange,
      onCropperReady,
      onZoom,
      save,
      defaultSize,
    };
  },
});
